import { Button } from "components/common/Button";
import React from "react";
import { Notification, NotificationTypeJS } from "target_wasm";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";

interface RequestActionsProps {
  notification: Notification;
  isProcessing: boolean;
  onAccept: (e: React.MouseEvent) => void;
  onDecline: (e: React.MouseEvent) => void;
  localRequestStatus?: string | null;
}

export const RequestActions: React.FC<RequestActionsProps> = ({
  notification,
  isProcessing,
  onAccept,
  onDecline,
  localRequestStatus,
}) => {
  const notificationType = notification.notification_type_js();
  const pendingInvites = useOrgContextStore((s) => s.pendingInvites);
  const invitesLoaded = useOrgContextStore((s) => s.invitesLoaded);
  if (
    notificationType === NotificationTypeJS.OrgScheduledForDeletion ||
    notificationType === NotificationTypeJS.SpaceScheduledForDeletion
  ) {
    // A cancelled deletion retracts THIS card rather than sending a second
    // one, so the only thing to render is what became of it.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WASM interop: get_data_js() returns untyped JS object
    const data = notification.get_data_js() as any;
    if ((data.status as string | undefined)?.toLowerCase() !== "cancelled")
      return null;
    return (
      <div className="flex items-center gap-2 min-h-7">
        <span className="px-2 py-1 text-accent-regular bg-accent-15 capitalize">
          cancelled
        </span>
      </div>
    );
  }

  if (
    notificationType !== NotificationTypeJS.TableRequest &&
    notificationType !== NotificationTypeJS.TableOwnershipTransferRequest &&
    notificationType !== NotificationTypeJS.OrgInvite
  )
    return null;

  if (notificationType === NotificationTypeJS.OrgInvite) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WASM interop: get_data_js() returns untyped JS object
    const data = notification.get_data_js() as any;
    // The stored payload never learns it was settled, so the pending list is
    // the truth — but only once it has actually been fetched. Before that an
    // empty list means "unknown", and hiding the buttons on it strands an
    // invite that arrived after the list was loaded. The door's 409 is the
    // real backstop; this only avoids offering an answer we know is spent.
    const settled =
      invitesLoaded && !pendingInvites.some((i) => i.org_id === data.org_id);
    if (settled && !localRequestStatus) return null;
    return (
      <div className="flex items-center gap-2 min-h-7">
        {localRequestStatus ? (
          <span className="px-2 py-1 text-accent-regular bg-accent-15 capitalize">
            {localRequestStatus}
          </span>
        ) : (
          <>
            <Button
              size="xs"
              variant="primary"
              onClick={onAccept}
              disabled={isProcessing}
            >
              Accept
            </Button>
            <Button
              size="xs"
              variant="secondary"
              onClick={onDecline}
              disabled={isProcessing}
            >
              Decline
            </Button>
          </>
        )}
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WASM interop: get_data_js() returns untyped JS object
  const data = notification.get_data_js() as any;
  const requestStatus =
    localRequestStatus ??
    (notificationType === NotificationTypeJS.TableOwnershipTransferRequest
      ? (data.status as string | undefined)
      : (data.request_status as string | undefined));
  const showStatusLabel =
    typeof requestStatus === "string" &&
    ["accepted", "rejected", "declined", "cancelled"].includes(
      requestStatus.toLowerCase(),
    );
  const displayRequestStatus =
    requestStatus?.toLowerCase() === "rejected" ? "declined" : requestStatus;

  if (notificationType === NotificationTypeJS.TableOwnershipTransferRequest) {
    const currentUserId = localStorage.getItem("clientID") ?? "";
    const isSender = data.in_email_details?.from_client_id === currentUserId;
    if (isSender) {
      const rawStatus =
        (data.status as string | undefined)?.toLowerCase() ?? "pending";
      const senderStatus = rawStatus === "rejected" ? "declined" : rawStatus;
      return (
        <div className="flex items-center gap-2 min-h-7">
          <span className="px-2 py-1 text-accent-regular bg-accent-15 text-xs capitalize">
            {senderStatus}
          </span>
        </div>
      );
    }
  }

  return (
    <div className="flex items-center gap-2 min-h-7">
      {isProcessing ? (
        <>
          <Button size="xs" variant="primary" onClick={onAccept} disabled>
            Accept
          </Button>
          <Button size="xs" variant="secondary" onClick={onDecline} disabled>
            Decline
          </Button>
        </>
      ) : showStatusLabel ? (
        <span className="px-2 py-1 text-accent-regular bg-accent-15 capitalize">
          {displayRequestStatus}
        </span>
      ) : (
        <>
          <Button
            size="xs"
            variant="primary"
            onClick={onAccept}
            disabled={isProcessing}
          >
            Accept
          </Button>
          <Button
            size="xs"
            variant="secondary"
            onClick={onDecline}
            disabled={isProcessing}
          >
            Decline
          </Button>
        </>
      )}
    </div>
  );
};
